import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../utils/firebaseConfig";
import {
  FaCheck,
  FaTimes,
  FaUsers,
  FaEnvelope,
  FaTrash,
  FaPrint,
} from "react-icons/fa";

const STATUS_LABEL = {
  pending: "Pending",
  accepted: "Accepted",
  rejected: "Rejected",
};

const isDev = process.env.NODE_ENV === "development";

// HTML escape so any user-supplied text is safe to embed in email markup.
const esc = (s) =>
  String(s == null ? "" : s).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );

// Firestore Timestamp | Date | string -> JS Date (or null if invalid).
const toJsDate = (d) => {
  if (!d) return null;
  const date = typeof d.toDate === "function" ? d.toDate() : new Date(d);
  return isNaN(date) ? null : date;
};

const formatDate = (d) => {
  const date = toJsDate(d);
  if (!date) return "";
  return date.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// True if the two dates fall on the same calendar day (local time).
const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Build a print-ready HTML document for today's accepted reservations and
// open it in a new window, triggering the browser's print / "Save as PDF"
// dialog. Nothing is sent anywhere — this is a local, on-demand service sheet.
const printTodaysReservations = (accepted) => {
  const now = new Date();

  const todays = accepted
    .map((r) => ({ ...r, _date: toJsDate(r.Date) }))
    .filter((r) => r._date && isSameDay(r._date, now))
    .sort((a, b) => a._date - b._date);

  const esc2 = esc; // reuse the HTML-escaper defined above
  const timeStr = (dt) =>
    dt.toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" });

  const heading = now.toLocaleDateString("el-GR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const totalCovers = todays.reduce(
    (sum, r) => sum + (parseInt(r.Persons, 10) || 0),
    0
  );

  const rowsHtml = todays.length
    ? todays
        .map(
          (r) => `
          <tr>
            <td class="c-time">${esc2(timeStr(r._date))}</td>
            <td class="c-name">${esc2(r.Name)}</td>
            <td class="c-guests">${esc2(r.Persons)}</td>
            <td class="c-email">${esc2(r.Email)}</td>
            <td class="c-note">${esc2(r.adminNote || "")}</td>
          </tr>`
        )
        .join("")
    : `<tr><td colspan="5" class="empty">Δεν υπάρχουν εγκεκριμένες κρατήσεις για σήμερα.</td></tr>`;

  const html = `<!doctype html>
<html lang="el">
<head>
<meta charset="utf-8" />
<title>Κρατήσεις — ${esc2(heading)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Caveat:wght@600;700&family=EB+Garamond:ital,wght@0,400;0,500;1,400&display=swap" rel="stylesheet" />
<style>
  /* The Old House — vintage booking sheet. Palette pulled from theme.css. */
  :root {
    --v-brown: #622f0a;
    --v-brown-dark: #3d1b04;
    --v-cream: #fad2a2;
    --v-cream-soft: #fdecd0;
    --v-olive: #6b7d3a;
    --v-terracotta: #c46d4f;
    --v-paper: #f5ebd6;
    --v-paper-soft: #fbf4e2;
    --v-ink: #2b1607;
    --v-ink-muted: #6b4d2e;
    --v-outline: rgba(98, 47, 10, 0.30);
    --v-outline-soft: rgba(98, 47, 10, 0.14);
  }
  @page { size: A4 portrait; margin: 16mm 34mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body {
    font-family: "EB Garamond", Georgia, "Times New Roman", serif;
    color: var(--v-ink);
    background: var(--v-paper-soft);
    line-height: 1.4;
  }
  /* On-screen preview mimics an A4 sheet; @page margins take over on print. */
  .sheet {
    max-width: 142mm;
    margin: 18px auto;
    padding: 26px 8px 30px;
    background: var(--v-paper-soft);
    border: 1px solid var(--v-outline);
    box-shadow: 0 0 0 4px var(--v-paper-soft), 0 0 0 5px var(--v-outline-soft);
  }
  @media print {
    .sheet { max-width: none; margin: 0; padding: 0; border: none; box-shadow: none; }
  }
  /* -------- Header -------- */
  header { text-align: center; padding-bottom: 14px; margin-bottom: 18px; }
  .rule-top {
    border: none;
    border-top: 3px double var(--v-brown);
    border-bottom: 1px solid var(--v-outline);
    height: 5px;
    margin: 0 0 16px;
  }
  .eyebrow {
    font-family: "Caveat", cursive;
    font-size: 22px;
    color: var(--v-terracotta);
    margin: 0 0 2px;
  }
  .brand {
    font-family: "DM Serif Display", Georgia, serif;
    font-size: 34px;
    letter-spacing: 0.5px;
    color: var(--v-brown);
    margin: 0;
    line-height: 1.05;
  }
  .ornament {
    color: var(--v-olive);
    font-size: 15px;
    letter-spacing: 6px;
    margin: 8px 0 6px;
  }
  .doc-date {
    font-size: 15px;
    font-style: italic;
    color: var(--v-ink-muted);
    margin: 0;
    text-transform: capitalize;
  }
  .summary {
    display: inline-block;
    margin: 12px auto 0;
    padding: 5px 16px;
    font-size: 12.5px;
    letter-spacing: 0.4px;
    color: var(--v-brown-dark);
    background: var(--v-cream-soft);
    border: 1px solid var(--v-outline-soft);
    border-radius: 999px;
  }
  /* -------- Table -------- */
  table { width: 100%; border-collapse: collapse; font-size: 13px; table-layout: fixed; }
  thead th {
    text-align: left;
    padding: 9px 10px;
    font-family: "EB Garamond", serif;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--v-cream-soft);
    background: var(--v-brown);
    border-bottom: 2px solid var(--v-brown-dark);
  }
  thead th:first-child { border-top-left-radius: 4px; }
  thead th:last-child { border-top-right-radius: 4px; }
  tbody td {
    padding: 9px 10px;
    border-bottom: 1px solid var(--v-outline-soft);
    vertical-align: top;
    word-break: break-word;
  }
  tbody tr:nth-child(even) { background: var(--v-cream-soft); }
  tbody tr:last-child td { border-bottom: 2px solid var(--v-outline); }
  .c-time { font-weight: 600; white-space: nowrap; width: 12%; color: var(--v-brown); }
  .c-name { width: 27%; font-weight: 500; }
  .c-guests { text-align: center; width: 10%; }
  .c-email { color: var(--v-ink-muted); width: 29%; font-size: 12px; }
  .c-note { color: var(--v-olive); font-style: italic; width: 22%; }
  .empty {
    text-align: center; padding: 28px; color: var(--v-ink-muted); font-style: italic;
    background: var(--v-cream-soft);
  }
  /* -------- Footer -------- */
  .rule-bottom {
    border: none;
    border-top: 1px solid var(--v-outline);
    margin: 20px 0 8px;
  }
  .foot-ornament { text-align: center; color: var(--v-olive); font-size: 13px; letter-spacing: 5px; margin: 4px 0 8px; }
  footer {
    text-align: center;
    font-size: 10.5px;
    font-style: italic;
    color: var(--v-ink-muted);
  }
</style>
</head>
<body>
  <div class="sheet">
    <hr class="rule-top" />
    <header>
      <p class="eyebrow">Φύλλο κρατήσεων</p>
      <h1 class="brand">The Old House</h1>
      <div class="ornament">❦ ⁂ ❦</div>
      <p class="doc-date">${esc2(heading)}</p>
      <span class="summary">${todays.length} ${
    todays.length === 1 ? "κράτηση" : "κρατήσεις"
  } &nbsp;·&nbsp; ${totalCovers} ${
    totalCovers === 1 ? "άτομο" : "άτομα"
  } &nbsp;·&nbsp; μόνο εγκεκριμένες</span>
    </header>
    <table>
      <thead>
        <tr>
          <th class="c-time">Ώρα</th><th class="c-name">Όνομα</th><th class="c-guests">Άτομα</th><th class="c-email">Email</th><th class="c-note">Σημείωση</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <hr class="rule-bottom" />
    <div class="foot-ornament">✦</div>
    <footer>Εκτυπώθηκε ${esc2(
      now.toLocaleString("el-GR")
    )} &nbsp;·&nbsp; The Old House</footer>
  </div>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (!win) {
    alert(
      "Επιτρέψτε τα αναδυόμενα παράθυρα (pop-ups) για να εκτυπώσετε τις σημερινές κρατήσεις."
    );
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  // Print once the document has rendered; guard against a double dialog if
  // both onload and the fallback timer fire.
  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    try {
      win.print();
    } catch (e) {
      /* no-op */
    }
  };
  win.onload = doPrint;
  setTimeout(doPrint, 500);
};

const renderCustomerEmail = ({ kind, reservation, adminNote }) => {
  const niceDate = esc(formatDate(reservation.Date));
  const greeting = `<p>Hi ${esc(reservation.Name)},</p>`;
  const guests = parseInt(reservation.Persons, 10) || 0;
  const guestsText = `${guests} guest${guests > 1 ? "s" : ""}`;
  const noteBlock = adminNote
    ? `<p><em>A note from us:</em><br/>${esc(adminNote).replace(
        /\n/g,
        "<br/>"
      )}</p>`
    : "";
  const signature = `<p>Warm regards,<br/>The Old House team</p>`;

  if (kind === "accepted") {
    return {
      subject: "Your reservation is confirmed",
      html: `${greeting}
        <p>Great news — your table for <strong>${guestsText}</strong> on
        <strong>${niceDate}</strong> is <strong>confirmed</strong>.</p>
        <p>We will hold your table for fifteen minutes past the
        reservation time. If anything changes on your end, just reply
        to this email.</p>
        ${noteBlock}
        <p>We look forward to seeing you!</p>
        ${signature}`,
    };
  }

  // rejected
  return {
    subject: "About your reservation request",
    html: `${greeting}
      <p>Thank you for your interest in <strong>The Old House</strong>.
      Unfortunately we are unable to confirm your request for
      <strong>${guestsText}</strong> on <strong>${niceDate}</strong>
      at this time.</p>
      ${noteBlock}
      <p>We hope to welcome you another time — please feel free to
      try a different date.</p>
      ${signature}`,
  };
};

export default function ManageReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [actionTarget, setActionTarget] = useState(null); // {reservation, kind}
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Real-time listener so admin sees new requests without refreshing.
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "reservations"),
      (snap) => {
        const rows = [];
        snap.forEach((d) => {
          const data = d.data();
          // Skip pure email payload docs (customer pending / accept / reject emails).
          if (data._emailOnly) return;
          // Skip any stray non-reservation docs.
          if (!data.Name) return;
          rows.push({ id: d.id, ...data });
        });
        // Newest pending first; accepted/rejected sorted by reservation date.
        rows.sort((a, b) => {
          const da =
            (a.Date_created && a.Date_created.toDate?.()) ||
            new Date(a.Date_created || 0);
          const db_ =
            (b.Date_created && b.Date_created.toDate?.()) ||
            new Date(b.Date_created || 0);
          return db_ - da;
        });
        setReservations(rows);
        setLoading(false);
      },
      (err) => {
        console.error("reservations listener error", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const grouped = useMemo(() => {
    const g = { pending: [], accepted: [], rejected: [] };
    for (const r of reservations) {
      const s = r.status || "pending";
      if (g[s]) g[s].push(r);
      else g.pending.push(r);
    }
    return g;
  }, [reservations]);

  const openAction = (reservation, kind) => {
    setActionTarget({ reservation, kind });
    setNoteText("");
  };

  const deleteReservation = async (reservation) => {
    const ok = window.confirm(
      "Delete this reservation for " +
        reservation.Name +
        "?\n\nThis cannot be undone."
    );
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "reservations", reservation.id));
      if (isDev) console.log("[admin] deleted reservation id=" + reservation.id);
    } catch (err) {
      console.error("[admin] delete failed:", err);
      alert("Could not delete. Please try again.");
    }
  };

  const closeAction = () => {
    if (submitting) return;
    setActionTarget(null);
    setNoteText("");
  };

  const confirmAction = async () => {
    if (!actionTarget) return;
    const { reservation, kind } = actionTarget;
    const newStatus = kind === "accept" ? "accepted" : "rejected";
    setSubmitting(true);
    try {
      if (isDev) console.log(
        `[admin] ${newStatus} reservation id=${reservation.id}` +
          (noteText.trim() ? " (with note)" : "")
      );

      // 1. Update the reservation status + admin note.
      await updateDoc(doc(db, "reservations", reservation.id), {
        status: newStatus,
        adminNote: noteText.trim(),
        decidedAt: serverTimestamp(),
      });
      if (isDev) console.log(`[admin] reservation ${reservation.id} status → ${newStatus}`);

      // 2. Send the customer email via an email-only doc in the same
      //    collection (the Firebase Trigger-Email extension picks this up).
      const { subject, html } = renderCustomerEmail({
        kind: newStatus,
        reservation,
        adminNote: noteText.trim(),
      });
      const emailRef = await addDoc(collection(db, "reservations"), {
        _emailOnly: true,
        to: [reservation.Email],
        message: { subject, html },
        relatedReservation: reservation.id,
        Date_created: new Date(),
      });
      if (isDev) console.log(
        `[admin] customer ${newStatus}-email doc written: id=${emailRef.id}`
      );

      closeAction();
    } catch (err) {
      console.error("[admin] Failed to update reservation:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { key: "pending", label: "Pending", count: grouped.pending.length },
    { key: "accepted", label: "Accepted", count: grouped.accepted.length },
    { key: "rejected", label: "Rejected", count: grouped.rejected.length },
  ];

  const rows = grouped[activeTab] || [];

  // Count of accepted reservations dining today, for the print button label.
  const todaysAcceptedCount = useMemo(() => {
    const now = new Date();
    return grouped.accepted.filter((r) => {
      const d = toJsDate(r.Date);
      return d && isSameDay(d, now);
    }).length;
  }, [grouped.accepted]);

  return (
    <div className="manage_reservations">
      <div className="reservations_toolbar">
        <button
          type="button"
          className="res_btn res_btn--print"
          onClick={() => printTodaysReservations(grouped.accepted)}
          title="Print today's accepted reservations"
        >
          <FaPrint /> Print today's reservations
          {todaysAcceptedCount > 0 ? ` (${todaysAcceptedCount})` : ""}
        </button>
      </div>

      <div className="reservations_tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            className={`reservations_tab${
              activeTab === t.key ? " is-active" : ""
            }`}
            onClick={() => setActiveTab(t.key)}
          >
            <span>{t.label}</span>
            <span className="reservations_tab__count">{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="reservations_empty">Loading reservations…</p>
      ) : rows.length === 0 ? (
        <p className="reservations_empty">No {activeTab} reservations.</p>
      ) : (
        <ul className="reservations_list">
          {rows.map((r) => (
            <li key={r.id} className="reservation_row" data-status={r.status}>
              <div className="reservation_row__main">
                <div className="reservation_row__head">
                  <h4 className="reservation_row__name">{r.Name}</h4>
                  <span
                    className="reservation_row__status"
                    data-status={r.status || "pending"}
                  >
                    {STATUS_LABEL[r.status || "pending"]}
                  </span>
                </div>
                <p className="reservation_row__date">{formatDate(r.Date)}</p>
                <div className="reservation_row__meta">
                  <span>
                    <FaUsers /> {r.Persons}
                  </span>
                  <span>
                    <FaEnvelope />{" "}
                    <a href={`mailto:${r.Email}`}>{r.Email}</a>
                  </span>
                </div>
                {r.adminNote && (
                  <p className="reservation_row__note">
                    <em>Note:</em> {r.adminNote}
                  </p>
                )}
              </div>

              <div className="reservation_row__actions">
                {(!r.status || r.status === "pending") && (
                  <>
                    <button
                      type="button"
                      className="res_btn res_btn--accept"
                      onClick={() => openAction(r, "accept")}
                    >
                      <FaCheck /> Accept
                    </button>
                    <button
                      type="button"
                      className="res_btn res_btn--reject"
                      onClick={() => openAction(r, "reject")}
                    >
                      <FaTimes /> Reject
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="res_btn res_btn--delete"
                  onClick={() => deleteReservation(r)}
                  title="Delete reservation"
                  aria-label="Delete reservation"
                >
                  <FaTrash />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {actionTarget && (
        <div
          className="reservation_modal__overlay"
          onClick={closeAction}
          role="presentation"
        >
          <div
            className="reservation_modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>
              {actionTarget.kind === "accept"
                ? "Accept reservation"
                : "Reject reservation"}
            </h3>
            <p className="reservation_modal__subtitle">
              <strong>{actionTarget.reservation.Name}</strong> —{" "}
              {formatDate(actionTarget.reservation.Date)} ·{" "}
              {actionTarget.reservation.Persons} guests
            </p>
            <label className="reservation_modal__label">
              Optional note for the customer
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={4}
                placeholder={
                  actionTarget.kind === "accept"
                    ? "e.g. We'll prepare the corner table for you."
                    : "e.g. We're fully booked that evening, but Sunday is available."
                }
              />
            </label>
            <div className="reservation_modal__actions">
              <button
                type="button"
                className="res_btn res_btn--ghost"
                onClick={closeAction}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`res_btn ${
                  actionTarget.kind === "accept"
                    ? "res_btn--accept"
                    : "res_btn--reject"
                }`}
                onClick={confirmAction}
                disabled={submitting}
              >
                {submitting
                  ? "Sending…"
                  : actionTarget.kind === "accept"
                  ? "Confirm & email customer"
                  : "Reject & email customer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
